import * as React from "react";
import L from "leaflet";
// Gives every polygon `editing` (the vertex handles); its CSS loads in main.tsx.
import "leaflet-draw";
import { CircleMarker, Marker, Polygon, useMap, useMapEvent } from "react-leaflet";
import { claimFence, generateCircleGeofence, type Geofence } from "~/shared/lib/geofence";
import { GeofenceToolbar, type GeofenceTool } from "./geofence-toolbar";
import { SITE_PIN_ICON } from "./site-pin-icon";

// leaflet-draw fires `edit` on a polygon when a vertex is moved, added or removed.
declare module "leaflet" {
  interface LeafletEventHandlerFnMap {
    edit?: LeafletEventHandlerFn;
  }
}

type LatLon = [number, number];

export type GeofenceDraft = { pin: LatLon | null; geofences: Geofence[] };

// Leaflet merges styles, so a finished fence has to clear the drawing's dashes explicitly.
const FENCE_STYLE = { color: "#2563eb", weight: 3, fillOpacity: 0.1, dashArray: undefined };
const DRAWING_STYLE = { ...FENCE_STYLE, dashArray: "6 6" };
const CORNER_STYLE = { color: "#2563eb", weight: 2, fillColor: "#fff", fillOpacity: 1 };

type EditablePolygon = L.Polygon & { editing: L.Handler };

const newId = () => Date.now().toString();

// Six decimals (~0.1m), as a 2GIS link gives the point.
const toPin = ({ lat, lng }: L.LatLng): LatLon => [Number(lat.toFixed(6)), Number(lng.toFixed(6))];

const ringOf = (layer: L.Polygon): LatLon[] =>
  (layer.getLatLngs()[0] as L.LatLng[]).map((p) => [p.lat, p.lng]);

/**
 * A site's pin and geofences, with the tools to place the pin and add and shape
 * the fences. Renders inside a MapContainer; without `onChange` it only shows them.
 */
export const GeofenceEditor = ({
  pin,
  geofences,
  onChange,
}: {
  pin: LatLon | null;
  geofences: Geofence[];
  onChange?: (draft: GeofenceDraft) => void;
}) => {
  const map = useMap();
  const [pressedTool, setTool] = React.useState<GeofenceTool | null>(null);
  // The fence being drawn corner by corner; it is a fence from its first click.
  const [drawingId, setDrawingId] = React.useState<string | null>(null);
  const layers = React.useRef(new Map<string, EditablePolygon>());
  // Read-only (no onChange), whatever was pressed before no longer applies.
  const tool = onChange ? pressedTool : null;
  const drawing = onChange ? geofences.find((fence) => fence.id === drawingId) : undefined;
  const placing = tool === "pin";

  const update = (next: Geofence[], nextPin = pin) => onChange?.({ pin: nextPin, geofences: next });
  // A fence whose ring the user changed is theirs: a reshaped suggestion stops being one.
  const withRing = (id: string, latlngs: Geofence["latlngs"]) =>
    geofences.map((g) => (g.id === id ? claimFence({ ...g, latlngs }) : g));

  // Under three corners a fence encloses nothing, so an unfinished one goes.
  const finishDrawing = () => {
    if (drawing && drawing.latlngs.length < 3) update(geofences.filter((g) => g !== drawing));
    setDrawingId(null);
  };

  const selectTool = (next: GeofenceTool | null) => {
    finishDrawing();
    setTool(next);
  };

  const deleteOnClick = (next: GeofenceDraft) => () => {
    if (tool !== "delete") return;
    onChange?.(next);
    setTool(null);
  };

  // leaflet-draw's handler keeps the ring its polygon had when it was made, and every
  // change here gives the polygon a new one: while editing, the handles are rebuilt on
  // the current rings after each change, the way leaflet-draw re-reads them itself.
  React.useEffect(() => {
    if (tool !== "edit") return;
    const editing = [...layers.current].filter(([id]) => id !== drawingId).map(([, l]) => l);
    editing.forEach((layer) => {
      layer.fire("revert-edited", { layer });
      layer.editing.enable();
    });
    return () => editing.forEach((layer) => layer.editing.disable());
  }, [tool, geofences, drawingId]);

  // While drawing, a double-click finishes the fence instead of zooming.
  const isDrawing = Boolean(drawing);
  React.useEffect(() => {
    if (!isDrawing || !map.doubleClickZoom.enabled()) return;
    map.doubleClickZoom.disable();
    return () => {
      map.doubleClickZoom.enable();
    };
  }, [map, isDrawing]);

  React.useEffect(() => {
    if (!placing) return;
    L.DomUtil.addClass(map.getContainer(), "leaflet-crosshair");
    return () => L.DomUtil.removeClass(map.getContainer(), "leaflet-crosshair");
  }, [map, placing]);

  // The fences travel with the pin: live while it is dragged, committed on drop.
  const fencesAt = (to: LatLon): Geofence[] => {
    if (!pin) return geofences;
    const [dLat, dLon] = [to[0] - pin[0], to[1] - pin[1]];
    return geofences.map((g) => ({
      ...g,
      latlngs: g.latlngs.map((p) => {
        const { lat, lng } = L.latLng(p);
        return [lat + dLat, lng + dLon];
      }),
    }));
  };

  // One use of the pin tool; a site without fences gets a 50m ring, 8 corners being
  // few enough to reshape by hand.
  const placePin = (to: LatLon) => {
    update(
      geofences.length
        ? fencesAt(to)
        : [{ id: newId(), latlngs: generateCircleGeofence(to[0], to[1], 50, 8) }],
      to,
    );
    setTool(null);
  };

  const isFence = (target: EventTarget | null) =>
    [...layers.current.values()].some((layer) => layer.getElement() === target);

  useMapEvent("click", (e) => {
    // The second click of a double-click is the double-click, not a corner.
    if (e.originalEvent.detail > 1) return;
    const at: LatLon = [e.latlng.lat, e.latlng.lng];
    if (placing) placePin(toPin(e.latlng));
    else if (drawing) update(withRing(drawing.id, [...drawing.latlngs, at]));
    else if (tool === "edit" && !isFence(e.originalEvent.target)) {
      const id = newId();
      update([...geofences, { id, latlngs: [at] }]);
      setDrawingId(id);
    }
  });

  useMapEvent("dblclick", finishDrawing);

  return (
    <>
      {onChange && <GeofenceToolbar tool={tool} onToolChange={selectTool} />}
      {geofences.map((fence) => (
        <Polygon
          key={fence.id}
          ref={(layer) => {
            if (layer) layers.current.set(fence.id, layer as EditablePolygon);
            else layers.current.delete(fence.id);
          }}
          positions={fence.latlngs}
          pathOptions={fence.id === drawingId ? DRAWING_STYLE : FENCE_STYLE}
          eventHandlers={{
            edit: (e) => update(withRing(fence.id, ringOf(e.target))),
            click: deleteOnClick({ pin, geofences: geofences.filter((g) => g !== fence) }),
          }}
        />
      ))}
      {/* The corners drawn so far; the first closes the fence once there are three. */}
      {drawing?.latlngs.map((corner, index) => (
        <CircleMarker
          key={index}
          center={corner}
          radius={index === 0 ? 6 : 4}
          bubblingMouseEvents={false}
          pathOptions={CORNER_STYLE}
          eventHandlers={{
            click: () => {
              if (index === 0 && drawing.latlngs.length >= 3) finishDrawing();
            },
          }}
        />
      ))}
      {pin && (
        <Marker
          position={pin}
          icon={SITE_PIN_ICON}
          draggable={placing}
          eventHandlers={{
            drag: (e) =>
              fencesAt(toPin(e.target.getLatLng())).forEach((g) =>
                layers.current.get(g.id)?.setLatLngs(g.latlngs),
              ),
            dragend: (e) => placePin(toPin(e.target.getLatLng())),
            // The delete tool takes the pin too; the fences stay.
            click: deleteOnClick({ pin: null, geofences }),
          }}
        />
      )}
    </>
  );
};
